import { useEffect, useState } from 'react';
import createNetworkingClient from 'utils/networking-client';
import { injectFacebookScript } from 'externalSocialPartners';
import { difference, isEmpty, split } from 'lodash-es';
import { isDev, timeout } from 'utils/dev-utils';
import { FOLLOWING_STATUS } from 'constants/follows/following-constants';
import { AuthorizationStatus } from './constants/auth-status-constants';

/**
 *  Permissions required by FB graph endpoints - see https://developers.facebook.com/docs/permissions/reference/
 */
const Permissions = Object.freeze({
  FRIEND_LIST: ['user_friends']
});

/**
 *
 * Requests for user permissions before attempting to interact with Facebook Graph API.
 * eg. athletes can request for their friends which will prompt them to give strava the right permissions
 * if the permissions are missing.
 *
 * DEVELOPMENT NOTE: The FB api doesn't work locally, so we get around it with local data, while still
 * preserving the flow as much as possible
 *
 * @param {Object} arg
 * @param {number} arg.appId - Strava app client id which changes based on the environment (dev, staging and prod).
 * @param {string} arg.version - Facebook graph version.
 * @returns {Object} response
 * @returns {boolean} response.ready - Whether FB javascript has been injected in the document.
 * @returns {string} response.authorizationStatus - enum value representing an athletes FB authorization state.
 * @returns {string} response.friends - Populated with a list of FB friends when requested by athlete.
 */
const useFacebook = ({ appId, version, locale }) => {
  const [authorizationStatus, setAuthorizationStatus] = useState(
    AuthorizationStatus.INITIATED
  );
  const [friends, setFriends] = useState([]);

  /**
   * During the first request to retrieve data from FB graph, we check if the athlete is already
   * connected to FB and if they have granted the right permissions for strava to retrieve
   * the requested data from their account without showing any FB verification/prompt UI.
   */
  const [isInitialRequest, setIsInitialRequest] = useState(true);

  /**
   * Inject FB graph api JS.
   *
   * Note - We need to do this for components that are mounted in haml templates since we are not
   * guaranteed that the script is available when the components attempt to request for FB graph data
   * on component mount.
   */
  useEffect(() => {
    injectFacebookScript({
      appId,
      version,
      locale,
      callback: () => setAuthorizationStatus(AuthorizationStatus.READY)
    });
  }, [appId, locale, version]);

  /**
   *
   * Checks to see if all the requested scopes were granted by the athlete.
   *
   * @param {Object} arg
   * @param {string} arg.grantedScopes - list of scopes that have been granted by the athlete
   * @param {string} arg.requestedScopes - list of scopes that are required by fb graph in order for strava
   *                 to retrieve requested user information.
   * @returns {boolean} - boolean value indicating whether the connected account has
   *                all the required permissions or not.
   */
  const checkPermissions = ({ grantedScopes, requestedScopes }) => {
    if (isEmpty(grantedScopes)) {
      return false;
    }
    return difference(requestedScopes, grantedScopes).length === 0;
  };

  /**
   * FB graph `permissions` endpoint returns permissions in the following format
   *  [{
   *    "permission": "user_friends",
   *    "status": "granted"
   *  }, {...}, ...]
   * See - https://developers.facebook.com/docs/facebook-login/web/permissions/
   *
   * Retrieves only granted permissions and process them into an array - eg. ['user_friends', ... ]
   * which is then used by #checkPermissions in #stravaFbProxyPostPermissions
   *
   * @param {Array} data - the data from the `permissions` endpoint response
   */
  const getGrantedScopes = (data) => {
    return data.filter((p) => p.status === 'granted').map((p) => p.permission);
  };

  /**
   * Uses `active` as a proxy to check if the given token has all the permissions
   * that are requested.
   *
   * @param {Object} arg
   * @param {string} arg.accessToken - token for connected FB account
   * @param {Array} arg.requestedScopes - List of permissions required for the requested data to be retrieved
   *                form FB graph
   * @returns {Promise<boolean>} - boolean value indicating whether the connected account has
   *                all the required permissions or not.
   */
  const stravaFbProxyPostPermissions = ({ accessToken, requestedScopes }) => {
    if (isDev()) {
      return new Promise((resolve) => {
        // import mock permissions data dynamically
        import('./data/facebook_permissions_sample.json').then((module) => {
          resolve(
            checkPermissions({
              grantedScopes: getGrantedScopes(module.default),
              requestedScopes
            })
          );
        });
      });
    }
    return new Promise((resolve, reject) => {
      createNetworkingClient()
        .post('/facebook_open_graph_actions/permissions', {
          access_token: accessToken
        })
        .then((response) => {
          if (response && response.status === 200) {
            resolve(
              checkPermissions({
                grantedScopes: getGrantedScopes(response.data),
                requestedScopes
              })
            );
          } else {
            reject(response.error);
          }
        })
        .catch((e) => reject(e));
    });
  };

  /**
   * Uses `active` as a proxy to retrieve friends data from FB graph
   *
   * @param {Object} arg
   * @param {string} arg.accessToken - token for connected FB account
   * @returns {Promise<Array>} - List of friends for the connected FB account.
   */
  const stravaFbProxyPostFriends = ({ accessToken }) => {
    if (isDev()) {
      return new Promise((resolve) => {
        // import mock friend data dynamically
        import('./data/facebook_friends_sample.json').then(async (module) => {
          await timeout(5);
          resolve(module.default);
        });
      });
    }
    return new Promise((resolve, reject) => {
      createNetworkingClient()
        .post('/facebook_open_graph_actions/friends', {
          access_token: accessToken
        })
        .then((response) => {
          if (response && response.status === 200) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  /**
   * Hits Strava endpoint to get the Strava athletes associated with FB user ids
   *
   * @param {Object} arg
   * @param {Array} arg.facebookUIDs - list of facebook UIDs
   * @returns {Promise<Array>} - list of FB friends as Strava athletes
   */
  const stravaUsersFromFbIds = ({ facebookUIDs }) => {
    return new Promise((resolve, reject) => {
      createNetworkingClient()
        .post('/athletes/facebook_friends_on_strava', {
          facebook_uids: facebookUIDs
        })
        .then((response) => {
          if (response && response.status === 200) {
            resolve(response.data);
          } else {
            reject(response.error);
          }
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  /**
   * Uses FB graph to prompt the user to log in with the given list of
   * permissions.
   *
   * @param {Array} requestedScopes - list of permissions that are required to retrieve requested data
   * @returns {Promise<Object>} -An object with the following attributes:
   *                            `hasPermissions` - a boolean indicating whether the athlete authorized all permissions
   *                            `accessToken` - token for the connected FB account
   */
  const fbGraphApiLogin = (requestedScopes) => {
    return new Promise((resolve) => {
      const options = { scope: requestedScopes, return_scopes: true };
      if (authorizationStatus === AuthorizationStatus.REQUEST_NEW_PERMISSIONS) {
        options.auth_type = 'rerequest';
      }
      window.FB.login((response) => {
        if (response.authResponse) {
          const { accessToken, grantedScopes } = response.authResponse;
          /**
           * grantedScopes is a comma-separated list of granted scopes eg.
           * `email, user_friends ...`
           *
           * The next step generates an array of permissions eg. eg. ['user_friends', ... ]
           * which is then used by #checkPermissions
           */
          const grantedScopesList = split(grantedScopes, ',');
          resolve({
            hasPermissions: checkPermissions({
              grantedScopes: grantedScopesList,
              requestedScopes
            }),
            accessToken
          });
        } else {
          resolve({ hasPermissions: false });
        }
      }, options);
    });
  };

  /**
   * Checks the permissions for an already logged-in athlete and request for new permissions if the permissions
   * are insufficient. A UI dialog is used to request for more permissions if necessary.
   *
   * @param {Array} requestedScopes - list of permissions that are required to retrieve requested data
   * @returns {Promise<Object>} -An object with the following attributes:
   *                            `status` - enum representing the authorization status (see auth-status-constants#AuthorizationStatus)
   *                            `accessToken` - token for the connected FB account
   * */
  const requestPermissions = (requestedScopes) => {
    return new Promise((resolve, reject) => {
      if (isInitialRequest) {
        /**
         * The component that is requesting data from FB has been mounted on the page, before prompting the user to
         * provide permissions, we check if they have already provided the permissions. If they are connected
         * but do not have the required permissions, then we update the status to `RequestNewPermissions` (which can
         * be used by the UI to show `Login with FB` button)
         */
        window.FB.getLoginStatus((response) => {
          // see https://developers.facebook.com/docs/facebook-login/web/ for possible status types
          if (response.status === 'connected') {
            const { accessToken } = response.authResponse;
            stravaFbProxyPostPermissions({
              accessToken,
              requestedScopes
            })
              .then((hasPermissions) => {
                const status = hasPermissions
                  ? AuthorizationStatus.AUTHORIZED
                  : AuthorizationStatus.REQUEST_NEW_PERMISSIONS;
                resolve({ status, accessToken });
              })
              .catch((e) => {
                reject(e);
              });
          } else {
            resolve({ status: AuthorizationStatus.REQUEST_NEW_PERMISSIONS });
          }
        });
        setIsInitialRequest(false);
      } else {
        /**
         * At this point, it has been determined that either the connected account does not have sufficient permissions or
         * an account is not connected at all.
         * The athlete is presented with the FB login button which when clicked will open a dialog requesting for
         * the required permissions. If they do not provide all of the permissions, the status will be updated to Unauthorized.
         *  */
        fbGraphApiLogin(requestedScopes).then(
          ({ hasPermissions, accessToken }) => {
            const status = hasPermissions
              ? AuthorizationStatus.AUTHORIZED
              : AuthorizationStatus.UNAUTHORIZED;
            resolve({ status, accessToken });
          }
        );
      }
    });
  };

  /**
   *  Request for friends data from FB graph
   */
  const requestFriends = () => {
    setAuthorizationStatus(AuthorizationStatus.READY); // reset auth status before flow
    requestPermissions(Permissions.FRIEND_LIST)
      .then(({ status, accessToken }) => {
        setAuthorizationStatus(status); // possible values - Authorized, UnAuthorized and NewPermissions
        if (status === AuthorizationStatus.AUTHORIZED) {
          stravaFbProxyPostFriends({ accessToken })
            .then((fbData) => {
              if (fbData.length > 0) {
                // pull out FB uids and get Strava athletes associated with them
                const facebookUIDs = fbData.map((friend) => friend.id);
                stravaUsersFromFbIds({ facebookUIDs })
                  .then((stravaData) => {
                    const camelCaseFriends = stravaData.map((athlete) => {
                      return {
                        id: athlete.id,
                        picture: athlete.picture,
                        followingStatus: athlete.following_status,
                        isFollowable:
                          athlete.following_status ===
                            FOLLOWING_STATUS.canFollow ||
                          athlete.following_status ===
                            FOLLOWING_STATUS.canRequest,
                        approveFollowers: athlete.approve_followers,
                        badge: athlete.badge,
                        displayName: athlete.display_name,
                        displayLocation: athlete.display_location
                      };
                    });
                    setFriends(camelCaseFriends);
                    setAuthorizationStatus(AuthorizationStatus.DATA_FETCHED);
                  })
                  .catch(() => {
                    // something went wrong while retrieving or processing Strava athletes from FB data
                    setAuthorizationStatus(AuthorizationStatus.ERROR); // TODO : log sentry error in the monorepo
                  });
              } else {
                // there were no FB uids to get Strava athletes from
                setFriends([]);
                setAuthorizationStatus(AuthorizationStatus.DATA_FETCHED);
              }
            })
            .catch(() => {
              // something went wrong while retrieving or processing friend data
              setAuthorizationStatus(AuthorizationStatus.ERROR); // TODO : log sentry error in the monorepo
            });
        }
      })
      .catch(() => {
        // something went wrong while requesting or checking permissions
        setAuthorizationStatus(AuthorizationStatus.ERROR); // TODO : log sentry error in the monorepo
      });
  };

  return {
    friends,
    authorizationStatus,
    requestFriends
  };
};

export default useFacebook;
